import React, { useCallback, useMemo, useState } from 'react';

import { CircularProgress, NoSsr, Tooltip } from '@mui/material';

import useHistoryOfChangesContext from 'pages/item/v2/hooks/use-history-of-changes-context';
import { HistoryOfChangesItem } from 'pages/item/v2/types/history-of-changes';
import isEmpty from 'utils/is-empty';

import LineChart from './LineChart';
import useStyles from './styles';
import { ChartData } from './types';

interface Props {
    title: string;
    scale?: number;
    getChart: (data: HistoryOfChangesItem[]) => ChartData | null;
}

const CHART_WIDTH = 268;
const CHART_PADDING = 8;

const TrendTooltip: React.FC<Props> = ({ getChart, title, children, scale }) => {
    const { classes } = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const { data, isLoading, load } = useHistoryOfChangesContext();
    const chart = useMemo(() => getChart(data.slice().reverse()) ?? [], [data, getChart]);

    const handleOpen = useCallback(() => {
        load();
        setIsOpen(true);
    }, [load]);

    const handleClose = useCallback(() => setIsOpen(false), []);

    const tooltip = useMemo(
        () => (
            <div className={classes.content} style={{ minWidth: CHART_WIDTH, maxWidth: CHART_WIDTH }}>
                <span className={classes.title}>{title}</span>
                {isLoading ? (
                    <div className={classes.spinner}>
                        <CircularProgress />
                    </div>
                ) : isEmpty(chart, { recursive: true }) ? (
                    <span className={classes.empty}>{'No history found'}</span>
                ) : (
                    <div className={classes.chart}>
                        <NoSsr>
                            <LineChart width={CHART_WIDTH} padding={CHART_PADDING} chart={chart} scale={scale} />
                        </NoSsr>
                    </div>
                )}
            </div>
        ),
        [chart, classes, isLoading, scale, title]
    );

    return (
        <Tooltip
            open={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            title={tooltip}
            classes={{ tooltip: classes.tooltip }}
        >
            <div data-role="ref-holder">{children}</div>
        </Tooltip>
    );
};

export default TrendTooltip;
